import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { APPLICATION_PATHS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import MenuSelector from '../../core/MenuSelector';
import Paging from '../../core/Paging';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { globalStyles } from '../../core/style/gstheme';
import { makeCancelable, processHashLocation, sortObjects, sortByProp } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import SearchResults from './SearchResults';
import ComponentSelector from '../../application-core/ComponentSelector';

const styles = () => {
  return Object.assign({
    advancedSearch: {
      marginRight: 30,
      padding: 12,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      minHeight: 520
    },
    searchResults: {
      marginTop: 12
    }
  }, globalStyles);
};

const searchProps = [
  {
    "value": "http://www.w3.org/2000/01/rdf-schema#label",
    "label": "Label",
    "indexed": true
  }
];

const resourceTypeOptions = [
  {

    "value": "http://www.w3.org/2002/07/owl#Class",
    "label": "Class"
  },
  {
    "value": "http://www.w3.org/2002/07/owl#ObjectProperty",
    "label": "Object Property"
  },
  {
    "value": "http://www.w3.org/2002/07/owl#DatatypeProperty",
    "label": "Datatype Property"
  },
  {
    "value": "http://www.w3.org/2002/07/owl#AnnotationProperty",
    "label": "Annotation Property"
  },
  {
    "value": "http://www.w3.org/2002/07/owl#NamedIndividual",
    "label": "Named Individual"
  },
  {
    "value": "http://www.w3.org/2000/01/rdf-schema#Datatype",
    "label": "Datatype"
  }
];

const searchPropIDs = searchProps.map(prop => prop.value);

const MAX_RESULTS = 50;

class AdvancedSearchContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      labelType: "http://www.w3.org/2000/01/rdf-schema#label",
      lang: this.props.defaultLang,
      resources: [],
      loadInfo: [],
      classResources: [],
      annotationPropertyInfo: [],
      text: "",
      ontology: "All",
      resourceType: "All",
      searchID: null,
      page: 1,
      noneFound: false,
      searchCount: 0,
      pageCount: 0,
      firstSearch: true,
      slowSearch: false,
      initialSearch: false,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    clearTimeout(this.searchTimeout);
  }

  componentDidMount() {
    const paramsMap = processHashLocation(this.props.location);
    const ontology = paramsMap.get("component");
    // We need to load annotation properties for ontology
    if (ontology !== undefined && ontology !== null && ontology !== "") {
      if (ontology === "All" || this.props.ontologies.some(tx => tx.id === ontology)) {
        this.handleOntologyChange(ontology);
        // If label type is label we need to also force a search
        const labelType = decodeURIComponent(paramsMap.get("labelType"));
        if (labelType === "http://www.w3.org/2000/01/rdf-schema#label") {
          this.handlePageChange();
        }
      }
      else {
        this.setState({
          isErrored: {
            message: "Invalid ontology URL for search"
          }
        });
      }
    }
    else {
      this.handlePageChange();
    }
  }

  handleSearchTooLong = () => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (!this.state.isLoading) return;
    this.setState({ initialSearch: true, firstSearch: false, slowSearch: true }, () => this.handlePageChange());
  };

  isStillSearching = () => {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(this.handleSearchTooLong, 20000);
  };

  handlePageChange = async () => {
    const noCount = this.state.initialSearch;
    if (this.state.firstSearch) this.isStillSearching();
    this.setState(
      { isLoading: true },
      () => {
        let search = [
          {
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: this.state.resourceType === "All" ?
              [
                "http://www.w3.org/2002/07/owl#Class",
                "http://www.w3.org/2002/07/owl#ObjectProperty",
                "http://www.w3.org/2002/07/owl#DatatypeProperty",
                "http://www.w3.org/2002/07/owl#AnnotationProperty",
                "http://www.w3.org/2002/07/owl#NamedIndividual",
                "http://www.w3.org/2000/01/rdf-schema#Datatype"
              ]
              :
              this.state.resourceType
          },
          {
            predicate: [this.state.labelType],
            ...(this.state.text !== "") && {
              value: this.state.text,
              isExactMatch: false
            }
          }
        ];
        if (this.state.ontology !== "All") {
          search.push({
            predicate: [
              "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
              "https://grafsync.graphifi.com/schema/gs/visibility"
            ],
            value: this.state.ontology
          });
        }
        const searchID = uuidv4();
        const noSortingNeeded = false;
        const isIndexedProp = searchProps.some(prop => prop.value === this.state.labelType && prop.indexed);
        this.setState({ searchID }, () => {
          this.doSearch({
            search: search,
            graph: [this.props.graph],
            exclude: [
              "https://graphifi.com/schema/xowl/topAnnotationProperty",
              "http://www.w3.org/2002/07/owl#topObjectProperty",
              "http://www.w3.org/2002/07/owl#topDataProperty",
              "http://www.w3.org/2002/07/owl#Thing"
            ],
            noCount: noSortingNeeded || noCount,
            retainOrder: noSortingNeeded,
            fast: noSortingNeeded || this.state.initialSearch,
            basicSort: true,
            predicates: ["http://www.w3.org/2000/01/rdf-schema#label"],
            page: this.state.page,
            language: this.state.lang,
            pageSize: MAX_RESULTS,
            ...(!isIndexedProp) && { searchMode: 'Compliant' },
            ...(!noSortingNeeded) && { sort: ["http://www.w3.org/2000/01/rdf-schema#label"] }
          }, searchID)
            .promise
            .then(data => {
              // Ignore 'old' 
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.results && data.results.length > 0) {
                const sortedResources = sortObjects([...data.results], 'label', this.state.lang, null, false);
                const resourceIDs = sortedResources.map(result => result.id);
                this.loadData(resourceIDs, {})
                  .promise
                  .then(() => this.setState({
                    resources: resourceIDs,
                    page: this.state.page,
                    pageCount: noCount ? 0 : data.pageCount,
                    searchCount: noCount ? 0 : data.searchCount,
                    isLoading: false
                  }))
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                this.setState({
                  isLoading: false,
                  page: 1,
                  resources: null,
                  pageCount: 1,
                  searchCount: 0
                });
              }
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        });
      });
  };

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(GS.search(payload, uuid));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  static getDerivedStateFromProps(props, state) {

    const paramsMap = processHashLocation(props.location);
    const lang = paramsMap.get("lang");
    const text = decodeURIComponent(paramsMap.get("text"));
    const page = paramsMap.get("page");
    const parsedPage = parseInt(page);
    const labelType = decodeURIComponent(paramsMap.get("labelType"));
    const resourceType = decodeURIComponent(paramsMap.get("resourceType"));
    const ontology = paramsMap.get("component");

    try {
      if (labelType !== "undefined" && labelType !== null && labelType !== "") {
        new URL(labelType);
      }
      if (resourceType !== "undefined" && resourceType !== null && resourceType !== "" && resourceType !== "All") {
        new URL(resourceType);
      }
      if (ontology !== undefined && ontology !== null && ontology !== "" && ontology !== "All") {
        new URL(ontology);
      }
    }
    catch (e) {
      return {
        isErrored: {
          message: "Invalid URL for search"
        }
      };
    }

    if ((lang !== state.lang) || (labelType !== state.labelType) || (text !== state.text) || (page !== state.page) ||
      (resourceType !== state.resourceType) || (ontology !== state.ontology)) {
      const newState = {
        ...(state.text !== text) && {
          text: (text === "undefined" || text === null || text === "" || text.length > 50) ?
            ""
            :
            text
        },
        ...(state.page !== page) && {
          page: page === undefined || page === null || page === "" || isNaN(parsedPage) || parsedPage < 1 || parsedPage > 100 ?
            1
            :
            parsedPage
        },
        ...(state.lang !== lang) && {
          lang: (lang === undefined || lang === null || lang === "" || !props.projectLang.includes(lang)) ?
            props.defaultLang
            :
            lang
        },
        ...(state.labelType !== labelType) && {
          labelType: (labelType === "undefined" || labelType === null || labelType === "" ||
            (!searchPropIDs.includes(labelType) && !state.annotationPropertyInfo.some(ap => ap.value === labelType))) ?
            "http://www.w3.org/2000/01/rdf-schema#label"
            :
            labelType
        },
        ...(state.resourceType !== resourceType) && {
          resourceType: (resourceType === "undefined" || resourceType === null || resourceType === ""
            || !resourceTypeOptions.some(rt => rt.value === resourceType)) ?
            "All"
            :
            resourceType
        },
        ...(state.ontology !== ontology) && {
          ontology: ontology === undefined || ontology === null || ontology === "" ||
            (!props.ontologies.some(tx => tx.id === ontology) && ontology !== "All") ?
            "All"
            :
            ontology
        }
      };
      return newState;
    }
    return null;
  }

  updateSearch = (page, text = null, labelType, resourceType, ontology, lang) => {
    this.setState({ initialSearch: false, firstSearch: false });
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/search#project=' + this.props.project;
    link += "&page=" + (page || this.state.page);
    link += "&text=" + (text !== null ? encodeURIComponent(text) : encodeURIComponent(this.state.text));
    link += "&lang=" + (lang || this.state.lang);
    link += "&labelType=" + (labelType ? encodeURIComponent(labelType) : encodeURIComponent(this.state.labelType));
    link += "&resourceType=" + (resourceType ? encodeURIComponent(resourceType) : encodeURIComponent(this.state.resourceType));
    link += "&component=" + (ontology || this.state.ontology);
    this.props.history.push(link);
  };

  componentDidUpdate(prevProps, prevState) {
    if ((prevState.lang !== this.state.lang) || (prevState.labelType !== this.state.labelType) || (prevState.text !== this.state.text)
      || (prevState.page !== this.state.page) || (prevState.resourceType !== this.state.resourceType) || (prevState.ontology !== this.state.ontology)) {
      this.handlePageChange();
    }
  }

  handlePagingChange = (page) => this.updateSearch(page);

  handleFilterChange = (text) => {
    if (this.state.slowSearch && text.length < 2) return;
    this.updateSearch(1, text);
  };

  handleTypeChange = (labelType) => this.updateSearch(1, null, labelType);

  handleResourceTypeChange = (resourceType) => this.updateSearch(1, null, null, resourceType);

  handleOntologyChange = async (ontology) => {
    try {
      let labelType = "http://www.w3.org/2000/01/rdf-schema#label";
      if (ontology === "All") {
        this.setState({
          annotationPropertyInfo: [],
          labelType
        });
        this.updateSearch(1, null, labelType, null, ontology);
        return;
      }
      await this.loadData([ontology]).promise;
      const ontologyData = GS.data(ontology, this.props.graph);
      let annotationPropertyInfo = [];
      if ("availableAnnotationProperty" in ontologyData && ontologyData.availableAnnotationProperty.length > 0) {
        await this.loadData(ontologyData.availableAnnotationProperty).promise;
        for (let prop of ontologyData.availableAnnotationProperty) {
          if (GS.isLoaded(prop, this.props.graph)) {
            const propData = GS.data(prop, this.props.graph);
            const propDataLabel = propData.label && propData.label[this.state.lang] ?
              propData.label[this.state.lang]
              :
              propData.label && propData.label["und"] ?
                propData.label && propData.label["und"]
                :
                propData.id;
            annotationPropertyInfo.push({
              "value": prop,
              "label": propDataLabel,
              "indexed": false
            });
          }
        }
        sortByProp(annotationPropertyInfo, "label", null, false);
      }
      // If new ontology also has annotation prop keep it displayed
      const paramsMap = processHashLocation(this.props.location);
      const paramLabelType = decodeURIComponent(paramsMap.get("labelType"));
      if (paramLabelType === "http://www.w3.org/2000/01/rdf-schema#label"
        || annotationPropertyInfo.some(ap => ap.value === paramLabelType)) {
        this.setState({
          annotationPropertyInfo
        });
        this.updateSearch(1, null, null, null, ontology);
      }
      else {
        // Else reset it to Label
        this.setState({
          annotationPropertyInfo,
          labelType
        });
        this.updateSearch(1, null, labelType, null, ontology);
      }
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  changeLanguage = (lang) => this.updateSearch(1, null, null, null, null, lang);

  render() {

    const { isLoading, resources, text, isErrored, resourceType, page, pageCount, labelType, ontology, slowSearch,
      lang, searchCount, initialSearch, annotationPropertyInfo } = this.state;
    const { defaultLang, graph, ontologies, project, classes } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    if (!ontologies || ontologies.length === 0) {
      return <div style={{ paddingRight: 30 }}>
        <ActionText offset="180px">There are no ontologies in this project.</ActionText>
      </div>;
    }

    const ontologyList = ontologies ?
      ontologies
        .map(ontology => ({
          value: ontology.id,
          label: ontology.title && ontology.title[defaultLang]
            ?
            ontology.title[defaultLang]
            :
            ontology.title && ontology.title["und"] ?
              ontology.title && ontology.title["und"]
              :
              ontology.id
        }))
        .sort((a, b) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) return - 1;
          return 1;
        })
      :
      [];

    const fromResult = (page - 1) * MAX_RESULTS + 1;
    const toResult = page * MAX_RESULTS > searchCount ? searchCount : page * MAX_RESULTS;
    const noSortingNeeded = false;

    return (
      <div className={classes.advancedSearch} data-test={initialSearch ? 'Initial' : 'NotInitial'}>
        <div data-test="search-filtering" className={classes.filterArea}>
          <Grid container spacing={2} classes={
            {
              "spacing-xs-2": this.props.classes.fullGrid
            }
          }>
            <Grid item xs={12}>
              <Typography className={classes.mainHeading} style={{ paddingTop: 0 }}>Advanced Search</Typography>
              {noSortingNeeded && resources &&
                <Typography data-test="enter-filter-text" variant="body2">Enter a label to get sorted results with a total count.</Typography>
              }
            </Grid>
            <Grid item xs={3}>
              <div style={{ paddingTop: 3 }}>
                <PagingFilter
                  autoFocus={true}
                  handleFilterChange={this.handleFilterChange}
                  text={text}
                  lang={lang}
                  minLength={slowSearch ? 2 : 0}
                  includeLabel={true}
                  label="Label"
                  maxLength={50} />
              </div>
            </Grid>
            <Grid item xs={3}>
              <ComponentSelector
                dataTest="ontology-selector"
                id={project}
                defaultLang={defaultLang}
                selected={ontology}
                components={ontologyList.map(o => o.value)}
                title="Ontologies"
                disabled={isLoading}
                graph={graph}
                handleChange={this.handleOntologyChange} />
            </Grid>
            <Grid item xs={2}>
              <MenuSelector
                dataTest="label-selector"
                label="Property"
                key={labelType}
                selected={labelType}
                handleChange={this.handleTypeChange}
                options={[
                  ...searchProps,
                  ...annotationPropertyInfo
                ]} />
            </Grid>
            <Grid item xs={2}>
              <MenuSelector
                dataTest="resource-type-selector"
                label="Resource Type"
                selected={resourceType}
                handleChange={this.handleResourceTypeChange}
                options={[
                  {
                    "value": "All",
                    "label": "All"
                  },
                  ...resourceTypeOptions
                ]} />
            </Grid>
            <Grid item xs={2}>
              <ProjectLanguageSelector
                key={lang}
                selectedLang={lang}
                includeLabel={true}
                onSelect={this.changeLanguage} />
            </Grid>
          </Grid>
        </div>
        <div style={{ marginBottom: 24 }}>
          {
            isLoading ?
              <div style={{ paddingTop: 160 }}>
                <Loading />
              </div>
              :
              <div className={classes.searchResults}>
                {!noSortingNeeded && searchCount > 0 &&
                  <Typography
                    role="status"
                    data-test="showing-results"
                    variant="body2"
                    className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount}</Typography>
                }
                <SearchResults
                  graph={graph}
                  project={project}
                  resources={resources}
                  ontologies={ontologies || []}
                  defaultLang={defaultLang}
                  viewLang={lang} />
              </div>
          }
        </div>
        {!isLoading && <Paging page={page} pageCount={pageCount} handlePageChange={this.handlePagingChange} />}
      </div>
    );
  }

}

AdvancedSearchContainer.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  projectLang: PropTypes.array.isRequired,
  entitlements: PropTypes.array.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  ontologies: PropTypes.array,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(AdvancedSearchContainer));